import { tradutorDe, type Tradutor } from '@vestigo/core/i18n-da-tela';
/**
 * ── «AINDA NÃO É HORA» — A TERCEIRA RAZÃO DE UM CARTÃO ESTAR TRANCADO ───────
 *
 * **A tese.** Um curso com data marcada não está recusado: está **marcado**. As
 * outras duas trancas da vitrine falam de um gesto que falta a quem lê — falta
 * comprar (e o clique leva à venda), falta fazer (e a régua desenha a
 * distância). Esta não fala de gesto nenhum: **não há nada que a pessoa possa
 * fazer para adiantá-la**, e nomear-lhe uma tarefa seria mentir. O que a tela
 * lhe deve é a data — e a distância até ela só onde a data sozinha não a dá.
 *
 * **O que este ficheiro recusa, e é a recusa principal.** A **régua** da tranca
 * de ordem. Ela existe porque ali há um mínimo e um lugar onde a pessoa está, e
 * a subtração entre os dois é o percurso. Aqui não há mínimo nem atual: há uma
 * data. Reusar a peça porque ela existe faria a tela desenhar «você está em 0%»
 * sobre uma coisa que não se cumpre estudando — o pior tipo de reúso, o que
 * empresta a FORMA de um fato a outro que não a tem.
 *
 * ## A precisão que não se promete
 *
 * A varredura que publica corre de 5 em 5 minutos: o curso «guardado» aparece *a
 * partir* da hora marcada. O caso desenhado aqui («anunciar») abre por
 * comparação de data e é exato — mas nem por isso a tela conta segundos. Um
 * relógio a correr num cartaz é peso, movimento e uma promessa de exatidão que
 * o produto inteiro não dá; e `prefers-reduced-motion` existe. A menor unidade
 * que esta tela diz é o **dia**, e a hora quem a diz é a frase do core.
 *
 * ## Quem diz o quê, depois de 24/09/2026
 *
 * A frase é do core e é **derivada aqui**, no relógio de quem lê (ver
 * `fraseDaEspera`). Desta camada sobram três coisas, e só três: ler o campo com
 * desconfiança, contar os dias, e decidir o **único degrau** em que a contagem
 * acrescenta alguma coisa à frase.
 */

/**
 * O agendamento, como o servidor o manda — e o tipo é o do CORE.
 *
 * Mesma lei do `TrancaDeOrdem`: uma segunda declaração aqui é exatamente o que
 * faz as duas pontas divergirem um campo de cada vez.
 */
export type { LancamentoDoCurso as Lancamento } from '@vestigo/core/area-lancamento-do-curso';

import { fraseDaHoraMarcada, type LancamentoDoCurso } from '@vestigo/core/area-lancamento-do-curso';
import type { Chave } from '@vestigo/core/i18n-da-tela';

/**
 * A frase da espera, derivada **no relógio de quem lê**.
 *
 * ## Por que a tela redige aqui, se quem desenha não redige
 *
 * Porque ela não redige: a **redação é uma só e mora no core**, e o que muda é
 * *quem a executa*. `fraseDaHoraMarcada` lê `getHours()`/`getDate()`, que falam
 * do relógio do processo — no servidor isso é UTC, e perto da meia-noite a
 * frase erra o dia para quem lê noutro fuso. É o defeito que o gotejamento da
 * aula tem documentado desde sempre (`fraseDoCadeado`, em `api.ts`), e a
 * arquitetura da tranca de ordem já o resolveu assim: a regra no core, a
 * execução na ponta que sabe que horas são para aquela pessoa.
 *
 * A `frase` do payload passa a ser **reserva**, para quem não pode derivar — o
 * telemóvel e o 403 —, e é o que sobra quando o `em` não se lê.
 */
export function fraseDaEspera(
  l: LancamentoDoCurso,
  agora: Date = new Date(),
  t: Tradutor = tradutorDe(null),
): string {
  const quando = l.em === '' ? null : new Date(l.em);
  /*
   * `l.frase` vem do SERVIDOR, e o servidor já a escreveu na língua desta
   * pessoa (`A8`): não se traduz outra vez aqui. O que se traduz é a reserva
   * da reserva — quando nem a frase do servidor veio.
   */
  if (quando === null || Number.isNaN(quando.getTime())) return l.frase || t('lancamento.semData');
  return fraseDaHoraMarcada(quando, agora, t);
}

/**
 * O que sobra quando o corpo veio sem a frase.
 *
 * Mesmo papel do `SEM_MOTIVO` da tranca de ordem: o `dist` do aluno e a API
 * podem ser de levas diferentes por uns minutos, e **a tranca é real** — o
 * servidor recusa a aula. Deixar o cartão virar link porque um campo faltou
 * seria oferecer o clique que a porta nega. Sem a frase, diz-se o que se sabe,
 * que é só a espécie da espera.
 */
export const SEM_DATA: Chave = 'estado.cursoAbreEmBreve';

/**
 * O agendamento lido com desconfiança — nunca campo a campo, à mão.
 *
 * Basta **um** dos dois para haver tranca a desenhar: com `em` a frase deriva-se
 * do instante, e sem `em` vale a que o servidor mandou. Os dois vazios é o curso
 * sem agendamento, que é o caso de quase todo curso — e aí `null`, que **não é
 * zero**.
 */
export function lerLancamento(bruto: unknown): LancamentoDoCurso | null {
  if (!bruto || typeof bruto !== 'object') return null;
  const l = bruto as Record<string, unknown>;
  const em = typeof l.em === 'string' ? l.em : '';
  const frase = typeof l.frase === 'string' ? l.frase : '';
  if (em === '' && frase === '') return null;
  return { em, frase };
}

/**
 * Quantos dias de CALENDÁRIO faltam, no fuso de quem lê. `null` = não dá para
 * saber, e aí não se afirma nada.
 *
 * ## Por que dias de calendário, e não milissegundos a dividir
 *
 * Porque «amanhã» é uma palavra do calendário, não uma medida de duração: às
 * 23h de segunda, a terça de manhã fica a 10 horas e continua a ser *amanhã*.
 * Dividir a diferença por 86 400 000 responde a outra pergunta e erra duas
 * vezes — na virada do dia e no domingo em que o dia tem 23 ou 25 horas.
 *
 * O índice do dia local sai do `getTimezoneOffset()` **de cada data**, e não de
 * um só: é isso que faz a conta sobreviver à mudança de horário entre hoje e a
 * data marcada.
 */
export function diasAte(em: string, agora: Date = new Date()): number | null {
  if (em === '') return null;
  const alvo = new Date(em);
  if (Number.isNaN(alvo.getTime())) return null;
  const diaLocal = (d: Date) =>
    Math.floor((d.getTime() - d.getTimezoneOffset() * 60_000) / 86_400_000);
  return diaLocal(alvo) - diaLocal(agora);
}

/**
 * A distância ao lado da frase — ou `null`, e aí ela cala. **Cala quase
 * sempre**, e é isso que a torna informação em vez de enfeite.
 *
 * ## A escada é a do core, e esta função vive no degrau que sobra
 *
 * `fraseDaHoraMarcada` já diz a distância onde ela é a melhor resposta:
 *
 * | faltam | a frase do core | esta contagem |
 * |---|---|---|
 * | já passou | «Já abriu.» | — |
 * | hoje | «Abre hoje às 9h» | — a HORA vale mais |
 * | 1 dia | «Abre amanhã» | — «faltam 1 dia» não é frase |
 * | 2 a 7 | «Abre em 5 dias» | — **seria a mesma medida duas vezes** |
 * | 8 ou mais | «Abre em 12 de março» | `faltam 43 dias` |
 *
 * Só o último degrau sobra, e é justamente onde a distância é o que a data não
 * dá: ninguém subtrai «12 de março» de hoje a olho. Nos outros, escrever a
 * contagem ao lado seria o acessório que se tira ao espelho antes de sair.
 *
 * O `>= 8` repete o degrau que o core escreve como `dias <= 7`. É a única
 * duplicação desta camada, e é declarada: o core não exporta a contagem, só a
 * frase — se um dia exportar, esta função passa a perguntar-lhe.
 */
export function quantoFalta(dias: number | null): string | null {
  return dias !== null && dias >= 8 ? `faltam ${dias} dias` : null;
}
